import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormRejectionRow, FormRejectionsResponse } from '@/api';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Section, SectionBody } from '@/components/Ui/Section';
import { UiLink } from '@/components/Ui/UiLink';
import type { FormSpec } from '@/lib/form-specs';
import { formatNumber } from '@/lib/money';
import { useRustyState } from '@/state';
import classes from './FormMessages.module.css';

// Fixed order and copy from the design; a reason with no row counts zero.
const REASONS = [
  { reason: 'origin_not_allowed', status: '403', saw: 'error', risk: true },
  { reason: 'field_rules', status: '400', saw: 'errorField', risk: false },
  { reason: 'trashed', status: '', saw: 'success', risk: false },
  { reason: 'captcha_missing', status: '400', saw: 'error', risk: false },
  { reason: 'rate_limited', status: '429', saw: 'error', risk: false },
  { reason: 'upload_refused', status: '400', saw: 'error', risk: true },
  { reason: 'insufficient_balance', status: '402', saw: 'balance', risk: true },
] as const;

type Reason = (typeof REASONS)[number];

export interface FormRejectionsProps {
  formId: string;
  /** Parsed fields, or null when the form has none; decides the trash and upload wording. */
  spec: FormSpec | null;
}

export function FormRejections({ formId, spec }: FormRejectionsProps) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<FormRejectionsResponse | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    useRustyState
      .getState()
      .api.getFormRejections(formId, 30, { onError: false })
      .then((res) => live && setData(res))
      .catch(() => live && setData(null));
    return () => {
      live = false;
    };
  }, [formId]);

  const fields = spec ? Object.values(spec.fields) : [];
  const anyTrash = fields.some((f) => f.on_fail === 'trash');
  const hasFileField = fields.some((f) => f.field === 'file');

  const meaning = ({ reason }: Reason, row?: FormRejectionRow) => {
    const count = row?.count ?? 0;
    const top = row?.top_detail;
    const topCount = row?.top_detail_count ?? 0;
    switch (reason) {
      case 'origin_not_allowed':
        if (count === 0) return null;
        return (
          <>
            {top
              ? t('formMessages.meaning.originFrom', { origin: top })
              : t('formMessages.meaning.originAny')}{' '}
            <UiLink to={`/account/forms/${formId}#origins`}>{t('formMessages.meaning.fix')}</UiLink>
          </>
        );
      case 'field_rules':
        return count > 0 && top
          ? t('formMessages.meaning.fieldRulesTop', {
              field: top,
              n: formatNumber(topCount, i18n.language),
            })
          : count === 0
            ? t('formMessages.meaning.fieldRulesNone')
            : null;
      case 'trashed':
        if (!anyTrash && count === 0) return t('formMessages.meaning.trashedNone');
        return count > 0 && top
          ? t('formMessages.meaning.trashedTop', {
              field: top,
              n: formatNumber(topCount, i18n.language),
            })
          : null;
      case 'captcha_missing':
        return count > 0 ? t('formMessages.meaning.captcha') : null;
      case 'rate_limited':
        return t('formMessages.meaning.rateLimits');
      case 'upload_refused':
        if (spec && !hasFileField) return t('formMessages.meaning.noFileField');
        return count > 0
          ? t('formMessages.meaning.uploadLimits')
          : t('formMessages.meaning.uploadsAccepted');
      case 'insufficient_balance': {
        if (!row || count === 0) return null;
        return (
          <>
            {t('formMessages.meaning.balance')}{' '}
            <UiLink to="/account/billing">{t('formMessages.meaning.billing')}</UiLink>
          </>
        );
      }
    }
  };

  const rows = REASONS.map((r) => ({ def: r, row: data?.rows.find((x) => x.reason === r.reason) }));

  return (
    <Section title={t('formMessages.rejectedTitle')} subtitle={t('formMessages.rejectedSubtitle')}>
      {data === null ? (
        <SectionBody>
          <EmptyText>{t('formMessages.rejectedUnavailable')}</EmptyText>
        </SectionBody>
      ) : (
        <DataTable
          caption={t('formMessages.rejectedTitle')}
          rows={rows}
          rowKey={(r) => r.def.reason}
          columns={[
            {
              key: 'reason',
              header: t('formMessages.colReason'),
              render: ({ def }) => (
                <>
                  <strong>{t(`formMessages.reason.${def.reason}`)}</strong>
                  {def.status && <span className={classes.mono}> {def.status}</span>}
                </>
              ),
            },
            {
              key: 'count',
              header: t('formMessages.colCount'),
              align: 'num',
              render: ({ def, row }) => {
                if (data === undefined) return '…';
                const count = row?.count ?? 0;
                return (
                  <span className={def.risk && count > 0 ? classes.risk : undefined}>
                    {formatNumber(count, i18n.language)}
                  </span>
                );
              },
            },
            {
              key: 'saw',
              header: t('formMessages.colVisitorSaw'),
              render: ({ def }) => t(`formMessages.saw.${def.saw}`),
            },
            {
              key: 'meaning',
              header: t('formMessages.colMeaning'),
              render: ({ def, row }) => meaning(def, row),
            },
          ]}
        />
      )}
    </Section>
  );
}
